import { keyToUrl } from '@repo/shared/keyutil';
import { useTheme } from '@repo/theme';
import { AdaptiveWidth, Pad, PadBox } from 'component/basics';
import { VoteRadioInput } from 'component/button/vote-radio-input';
import { Heading } from 'component/text';
import { useCommentTeaser } from 'data/spaces';
import type { ArticleTeaserConfig } from 'feature/teaser/editorial-question';
import { CommentStack, QuestionInfo, WideQuestionTeaserStyle } from 'feature/teaser/editorial-question';
import { useCallback, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { TeaserScreen } from 'system/teaser';
import { useBroadcastSubscription, VOTE_CHANNEL } from 'util/broadcast';
import { useDatastore, useInstanceKey } from 'util/datastore';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';

type DialogueAdaptiveLayoutArticleQuestionTeaserWithCommentProps = {
    spaceKey: string;
};

export function DialogueAdaptiveLayoutArticleQuestionTeaserWithComment({
    spaceKey,
}: DialogueAdaptiveLayoutArticleQuestionTeaserWithCommentProps) {
    const instanceKey = useInstanceKey();
    const entryUrl = keyToUrl(instanceKey);

    const { name: questionText, preview: commentPreview, topLevelCount: commentCount } = useCommentTeaser(spaceKey);

    return (
        <TeaserScreen>
            <DialogueAdaptiveLayoutQuestionTeaser
                commentCount={commentCount}
                commentPreview={commentPreview}
                entryUrl={entryUrl}
                questionKey={spaceKey}
                questionText={questionText}
            />
        </TeaserScreen>
    );
}

export const DialogueQuestionTeaserFeature: Feature<ArticleTeaserConfig, {}> = {
    name: 'Question Dialogue',
    key: 'dialoguegreenquestionteaser',
    providesConfig: {
        teaserSingleQuestion: DialogueAdaptiveLayoutArticleQuestionTeaserWithComment,
    },
};

type DialogueEmbeddedQuestionTeaserConfig = {
    teaserSingleQuestion: React.ComponentType<DialogueAdaptiveLayoutQuestionTeaserProps>;
};

export const DialogueEmbeddedQuestionTeaserFeature: Feature<ArticleTeaserConfig, DialogueEmbeddedQuestionTeaserConfig> = {
    name: 'Dialogue Embedded Teaser',
    key: 'dialogueembeddedquestionteaser',
    dev: true,
    providesConfig: {
        teaserSingleQuestion: DialogueAdaptiveLayoutArticleQuestionTeaserWithComment,
    },
};

type DialogueQuestionInfoProps = {
    questionText: string;
    narrow?: boolean;
};

export function DialogueQuestionInfo({ questionText, narrow = false }: DialogueQuestionInfoProps) {
    const textColor = useTheme().colors.colorTextOnEntryPointColorBg;

    return (
        <View>
            <Pad size={narrow ? 20 : 24} />
            <Heading color={textColor} text={questionText} type='large' weight='medium'></Heading>
            <Pad size={8} />
        </View>
    );
}

type DialogueWideQuestionTeaserWithCommentProps = {
    questionText: string;
    commentPreview: any;
    commentCount: number;
    selectedIndex: string | null;
    onVote?: (voteIndex: string) => void;
};

export function DialogueWideQuestionTeaserWithComment({
    questionText,
    commentPreview,
    commentCount,
    selectedIndex,
    onVote,
}: DialogueWideQuestionTeaserWithCommentProps) {
    const s = WideQuestionTeaserStyle;

    const { colors } = useTheme();

    return (
        <View style={s.outer}>
            <View style={[{ backgroundColor: colors.colorEntryPointColorGreen }, s.left]}>
                <QuestionInfo commentCount={commentCount} questionText={questionText} />
                <View style={{ height: 20 }} />
                <View style={{ gap: 8 }}>
                    <VoteRadioInput
                        label={t('voting.yes', 'Yes')}
                        onPress={() => onVote?.('0')}
                        selected={selectedIndex === '0'}
                        stopPropagation
                        testID='vote-yes'
                    />
                    <VoteRadioInput
                        label={t('voting.no', 'No')}
                        onPress={() => onVote?.('1')}
                        selected={selectedIndex === '1'}
                        stopPropagation
                        testID='vote-no'
                    />
                </View>
            </View>
            <View style={[{ backgroundColor: colors.colorEntryPointColorGreenTint }, s.right]}>
                <CommentStack commentPreview={commentPreview} />
            </View>
        </View>
    );
}

type DialogueNarrowQuestionTeaserWithCommentProps = {
    questionText: string;
    commentPreview: any;
    commentCount: number;
    selectedIndex: string | null;
    onVote?: (voteIndex: string) => void;
};

export function DialogueNarrowQuestionTeaserWithComment({
    questionText,
    commentPreview,
    commentCount,
    selectedIndex,
    onVote,
}: DialogueNarrowQuestionTeaserWithCommentProps) {
    const s = DialogueNarrowQuestionTeaserStyle;

    const { colors } = useTheme();

    return (
        <View style={[s.top, { backgroundColor: colors.colorEntryPointColorGreen }]}>
            <PadBox horiz={20} vert={20}>
                <QuestionInfo commentCount={commentCount} narrow questionText={questionText} />
                <View style={{ height: 20 }} />
                <View>
                    <VoteRadioInput
                        label={t('voting.yes', 'Yes')}
                        onPress={() => onVote?.('0')}
                        selected={selectedIndex === '0'}
                        stopPropagation
                        testID='vote-yes'
                    />
                    <View style={{ height: 8 }} />
                    <VoteRadioInput
                        label={t('voting.no', 'No')}
                        onPress={() => onVote?.('1')}
                        selected={selectedIndex === '1'}
                        stopPropagation
                        testID='vote-no'
                    />
                </View>
            </PadBox>
            <View style={[s.bottom, { backgroundColor: colors.colorEntryPointColorGreenTint }]}>
                <PadBox bottom={20}>
                    <CommentStack commentPreview={commentPreview} />
                </PadBox>
            </View>
        </View>
    );
}

const DialogueNarrowQuestionTeaserStyle = StyleSheet.create({
    top: {
        borderRadius: 10,
    },
    bottom: {
        padding: 20,
        flex: 1,
        justifyContent: 'center',
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 10,
    },
});

type DialogueAdaptiveLayoutQuestionTeaserProps = {
    questionText: string;
    questionKey: string;
    entryUrl: string;
    commentPreview: any;
    commentCount: number;
};

export function DialogueAdaptiveLayoutQuestionTeaser({
    questionText,
    questionKey,
    entryUrl,
    commentPreview,
    commentCount,
}: DialogueAdaptiveLayoutQuestionTeaserProps) {
    const [narrow, setNarrow] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState<string | null>(null);
    const datastore = useDatastore();

    useBroadcastSubscription<{ voteIndex: string }>(
        VOTE_CHANNEL,
        useCallback((data) => {
            setSelectedIndex(data.voteIndex);
        }, []),
    );

    function onPress() {
        const globalParams = { entryUrl };
        datastore.gotoInstance({ structureKey: 'question', instanceKey: questionKey, globalParams });
    }

    function onVote(voteIndex: string) {
        setSelectedIndex(voteIndex);
        const globalParams = { entryUrl };
        datastore.gotoInstance({ structureKey: 'question', instanceKey: questionKey, params: { vote: voteIndex }, globalParams });
    }

    return (
        <AdaptiveWidth breakpoint={540} setNarrow={setNarrow}>
            <Pressable onPress={onPress} testID='question-teaser'>
                {narrow ? (
                    <DialogueNarrowQuestionTeaserWithComment
                        commentCount={commentCount}
                        commentPreview={commentPreview}
                        onVote={onVote}
                        questionText={questionText}
                        selectedIndex={selectedIndex}
                    />
                ) : (
                    <DialogueWideQuestionTeaserWithComment
                        commentCount={commentCount}
                        commentPreview={commentPreview}
                        onVote={onVote}
                        questionText={questionText}
                        selectedIndex={selectedIndex}
                    />
                )}
            </Pressable>
        </AdaptiveWidth>
    );
}
